import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeftIcon, DeviceMobileIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { Notice, PillButton } from '@/components/kit';
import { ClientHeader } from '@/features/studio/ClientHeader';
import { mediaUrl } from '@/lib/media';
import { useDocumentTitle } from '@/lib/hooks';
import { useInstallPrompt } from '@/lib/pwa/install';
import { isIos, isStandalone } from '@/lib/pwa/platform';
import { useStudio } from '@/features/studio/studio-data';

type Platform = 'ios' | 'android' | 'desktop';

const STEPS: Record<Platform, string[]> = {
  ios: [
    'Откройте эту страницу в Safari.',
    'Нажмите «Поделиться» внизу экрана.',
    'Выберите «На экран „Домой“» и нажмите «Добавить».',
    'Студия появится на экране как отдельное приложение — с напоминаниями о записи.',
  ],
  android: [
    'Откройте эту страницу в Chrome.',
    'Нажмите «Установить» ниже или меню ⋮ → «Добавить на гл. экран».',
    'Подтвердите установку — иконка появится рядом с другими приложениями.',
  ],
  desktop: [
    'Откройте страницу в Chrome, Edge или Яндекс Браузере.',
    'Нажмите значок установки в адресной строке или «Установить» ниже.',
    'Приложение откроется в своём окне без вкладок браузера.',
  ],
};

/** «Добавьте студию на главный экран»: real prompt where the browser offers one, steps elsewhere. */
export function InstallPage() {
  const studio = useStudio();
  const install = useInstallPrompt();
  useDocumentTitle(`Приложение — ${studio.name}`);
  const [platform, setPlatform] = useState<Platform>(() =>
    isIos() ? 'ios' : /android/i.test(navigator.userAgent) ? 'android' : 'desktop',
  );
  const icon = mediaUrl(studio.logoPath);
  const installed = isStandalone();

  return (
    <div className="ui-page">
      <ClientHeader />
      <Link to={`/s/${studio.slug}/`} className="ui-back">
        <ArrowLeftIcon size={16} aria-hidden />
        На главную
      </Link>

      <div className="ui-card" style={{ padding: 'var(--spacing-5)' }}>
        <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
          <div className="ui-row">
            {icon ? <img className="ui-appicon" src={icon} alt="" crossOrigin="anonymous" /> : null}
            <span className="ui-stack" style={{ ['--ui-gap' as string]: 0 }}>
              <span className="ui-title">{studio.shortName}</span>
              <span className="ui-caption">Приложение студии</span>
            </span>
          </div>
          <h1 className="ui-h1">Добавьте студию на главный экран</h1>
          <p className="ui-muted">Своя иконка на телефоне: услуги, запись и напоминания — в одно касание.</p>
          <div className="ui-row" style={{ ['--ui-gap' as string]: '10px' }}>
            <DeviceMobileIcon size={20} aria-hidden />
            <span className="ui-text">Пара шагов в браузере, без магазина приложений</span>
          </div>
        </div>
      </div>

      {installed ? (
        <Notice tone="info" title="Приложение уже установлено">
          Вы открыли студию с экрана телефона.
        </Notice>
      ) : null}

      {install.canPrompt && !installed ? (
        <PillButton variant="primary" size="lg" block icon={DownloadSimpleIcon} onClick={() => void install.prompt()}>
          Установить
        </PillButton>
      ) : null}

      <section className="ui-stack" aria-label="Как установить">
        <h2 className="ui-h2">Как установить</h2>
        <div className="ui-segmented ui-segmented--inset" role="tablist" aria-label="Устройство">
          {(
            [
              ['ios', 'iPhone / iPad'],
              ['android', 'Android'],
              ['desktop', 'Компьютер'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              className="ui-segment"
              aria-selected={platform === key}
              onClick={() => setPlatform(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <ol className="ui-card ui-stack" role="tabpanel" style={{ margin: 0, listStyle: 'none' }}>
          {STEPS[platform].map((step, index) => (
            <li key={step} className="ui-row" style={{ alignItems: 'flex-start' }}>
              <span
                className="ui-badge-icon ui-badge-icon--sm ui-badge-icon--soft ui-num"
                style={{ fontSize: 13, fontWeight: 700 }}
              >
                {index + 1}
              </span>
              <span className="ui-text">{step}</span>
            </li>
          ))}
        </ol>
        <p className="ui-caption">Бесплатно. Установка идёт через браузер, данные не покидают студию.</p>
      </section>
    </div>
  );
}
